/**
 * EditPlantEnum Component
 * 
 * Dialog component for editing existing plant enum records.
 * Similar to AddPlantEnum but pre-populates form with existing data.
 * 
 * Features:
 * - Loads existing plant data into form
 * - Allows updating all plant properties
 * - Supports image updates (replaces existing images)
 * - Validates input data
 * - Updates Firestore and Storage
 * - Provides user feedback via snackbars
 * 
 * @param {boolean} isDialogOpen - Controls dialog visibility
 * @param {Function} handleClose - Function to close the dialog
 * @param {Object} plantEnumInitData - Existing plant data to edit
 */
import React, { useEffect, useState } from "react";

import { doc, updateDoc } from "firebase/firestore";
import { useDropzone } from "react-dropzone";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Switch, Snackbar } from "@mui/material";
import Resizer from "react-image-file-resizer";

import {
  ref,
  getDownloadURL,
  deleteObject,
  uploadBytes,
} from "firebase/storage";

import { ImageLoader } from "../../../../components/ui/img/ImageLoader";
import { firebaseStorage } from "../../../../components/firebase/storage";
import { firebaseStore } from "../../../../components/firebase/firestore";
import { primaryColorTextField } from "../../../../components/theme/globalStyles";
import { plantCategoryEnums } from "../../../../components/utils/enums";

import "./PlantEnumsComp.css";

function EditPlantEnum({ isDialogOpen, handleClose, plantEnumInitData }: any) {
  /**
   * Dropzone for icon image upload
   */
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 1,
    onDrop: (files) => onIconDrop(files),
  });
  
  /**
   * Dropzone for main image upload
   */
  const { getRootProps: getRootPropsImage, getInputProps: getInputPropsImage } =
    useDropzone({
      accept: {
        "image/jpeg": [],
        "image/png": [],
      },
      maxFiles: 1,
      onDrop: (files) => onImageDrop(files),
    });
  
  /**
   * Dropzone for image gallery (max 5 images)
   */
  const {
    getRootProps: getRootPropsImageArray,
    getInputProps: getInputPropsImageArray,
  } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxFiles: 5,
    onDrop: (files) => onImageDropArray(files),
  });
  
  // UI state
  const [dialogLoading, setDialogLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Basic plant information state
  const [plantName, setPlantName] = useState<string>("");
  const [scientificName, setScientificName] = useState<string>("");
  const [plantSubtitle, setPlantSubtitle] = useState<string>("");
  const [plantDescription, setPlantDescription] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  
  // Growth timeline state (in days)
  const [growTimeBabyMin, setGrowTimeBabyMin] = useState<number>(3);
  const [growTimeBabyMax, setGrowTimeBabyMax] = useState<number>(14);
  const [growTimeVegetativeMin, setGrowTimeVegetativeMin] = useState<number>(20);
  const [growTimeVegetativeMax, setGrowTimeVegetativeMax] = useState<number>(30);
  const [floweringMin, setFloweringMin] = useState<number>(30);
  const [floweringMax, setFloweringMax] = useState<number>(40);
  
  // Image state
  const [iconPath, setIconPath] = useState<any>(null);
  const [imagePath, setImagePath] = useState<any>(null);
  const [imagePathArray, setImagePathArray] = useState<any[]>([]);
  
  // Multi-value characteristics
  const [nutrientActual, setNutrientActual] = useState<string>("");
  const [nutrientList, setNutrientList] = useState<string[]>([]);
  const [kitchenList, setKitchenList] = useState<string[]>([]);
  const [kitchenActual, setKitchenActual] = useState<string>("");
  const [flavourList, setFlavourList] = useState<string[]>([]);
  const [flavourActual, setFlavourActual] = useState<string>("");
  const [colorList, setColorList] = useState<string[]>([]);
  const [colorActual, setColorActual] = useState<string>("");
  const [allergeenList, setAllergeenList] = useState<string[]>([]);
  const [allergeenActual, setAllergeenActual] = useState<string>("");
  
  // Edibility
  const [isEdible, setIsEdible] = useState<boolean>(false);

  /**
   * Initialize form with existing plant data when component mounts
   * Populates all fields with current values from plantEnumInitData
   * Handles URL-based images by creating special file objects
   */
  useEffect(() => {
    if (plantEnumInitData) {
        console.log(plantEnumInitData);
      setPlantName(plantEnumInitData.name);
      setScientificName(plantEnumInitData.scientificName);
      setPlantSubtitle(plantEnumInitData.subtitle);
      setPlantDescription(plantEnumInitData.description);
      setCategory(plantEnumInitData.category);
      setGrowTimeBabyMin(plantEnumInitData.growTimeBaby.min);
      setGrowTimeBabyMax(plantEnumInitData.growTimeBaby.max);
      setGrowTimeVegetativeMin(plantEnumInitData.growTimeVegetative.min);
      setGrowTimeVegetativeMax(plantEnumInitData.growTimeVegetative.max);
      setFloweringMin(plantEnumInitData.growTimeFlowering.min);
      setFloweringMax(plantEnumInitData.growTimeFlowering.max);
      setColorList(plantEnumInitData.color);
      setFlavourList(plantEnumInitData.flavour);
      setNutrientList(plantEnumInitData.nutrients);
      setKitchenList(plantEnumInitData.kitchen);
      setAllergeenList(plantEnumInitData.allergeens);
      setIsEdible(plantEnumInitData.isEdible);
      // Set Icon path if url exists
      if (plantEnumInitData.icon) {
        setIconPath({ url: plantEnumInitData.icon, isUrl: true });
      }
      // Set Image path if url exists
      if (plantEnumInitData.image) {
        setImagePath({ url: plantEnumInitData.image, isUrl: true });
      }
      // Set Image array path if url exists
      if (plantEnumInitData.images) {
        const tempArray: any = [];
        for (let i = 0; i < plantEnumInitData.images.length; i++) {
          tempArray.push({ url: plantEnumInitData.images[i], isUrl: true });
        }
        setImagePathArray(tempArray);
      }
    }
  }, []);

  /**
   * Add a text value to a list with validation
   * Same validation as AddPlantEnum component
   * 
   * @param value - String to add
   * @param list - Current list state
   * @param setList - State setter for the list
   * @param setValue - State setter to clear input field
   */
  const handleAddToList = (
    value: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setValue: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const tempList = [...list];
    if (
      value !== "" &&
      !tempList.includes(value) &&
      value.length > 2 &&
      value.length < 1000 &&
      tempList.length < 10
    ) {
      tempList.push(value);
      setList(tempList);
      setValue("");
    }
  };

  // Remove text value from state list
  const handleRemoveFromList = (
    index: number,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const tempList = [...list];
    tempList.splice(index, 1);
    setList(tempList);
  };

  const onIconDrop = async (files: any) => {
    const file = files[0];
    setIconPath(file);
  };

  const onImageDrop = async (files: any) => {
    const file = files[0];
    setImagePath(file);
  };

  const onImageDropArray = async (files: any) => {
    if (files.length > 5) {
      return;
    }
    if (files.length + imagePathArray.length > 5) {
      return;
    }
    const tempArray = [...imagePathArray];
    files.forEach((file: any) => {
      tempArray.push(file);
    });
    setImagePathArray(tempArray);
  };

  const onDeleteFromImageArray = (index: number) => {
    const tempList = [...imagePathArray];
    tempList.splice(index, 1);
    setImagePathArray(tempList);
  };

  // Reset to initial state from plantEnumData
  const onReset = () => {
    setPlantName(plantEnumInitData.name);
    setScientificName(plantEnumInitData.scientificName);
    setPlantSubtitle(plantEnumInitData.subtitle);
    setPlantDescription(plantEnumInitData.description);
    setCategory(plantEnumInitData.category);
    setGrowTimeBabyMin(plantEnumInitData.growTimeBaby.min);
    setGrowTimeBabyMax(plantEnumInitData.growTimeBaby.max);
    setGrowTimeVegetativeMin(plantEnumInitData.growTimeVegetative.min);
    setGrowTimeVegetativeMax(plantEnumInitData.growTimeVegetative.max);
    setFloweringMin(plantEnumInitData.growTimeFlowering.min);
    setFloweringMax(plantEnumInitData.growTimeFlowering.max);
    setColorList(plantEnumInitData.color);
    setFlavourList(plantEnumInitData.flavour);
    setNutrientList(plantEnumInitData.nutrients);
    setKitchenList(plantEnumInitData.kitchen);
    setAllergeenList(plantEnumInitData.allergeens);
    setIsEdible(plantEnumInitData.isEdible);
    // Set Icon path if url exists
    if (plantEnumInitData.icon) {
      setIconPath({ url: plantEnumInitData.icon, isUrl: true });
    }
    // Set Image path if url exists
    if (plantEnumInitData.image) {
      setImagePath({ url: plantEnumInitData.image, isUrl: true });
    }
    // Set Image array path if url exists
    if (plantEnumInitData.images) {
      const tempArray: any = [];
      for (let i = 0; i < plantEnumInitData.images.length; i++) {
        tempArray.push({ url: plantEnumInitData.images[i], isUrl: true });
      }
      setImagePathArray(tempArray);
    }
  };

  const resizeFile = (file: any, size: number, type: string) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        size,
        size,
        type,
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "blob"
      );
    });

  const checkIfLogoExists = async (path: string) => {
    try {
      const imageRef = ref(firebaseStorage, path);
      await getDownloadURL(imageRef);
      return true;
    } catch (err) {
      return false;
    }
  };

  const uploadImage = async (
    file: any,
    path: string,
    size: number,
    type: string
  ) => {
    const storageRef = ref(firebaseStorage, path);
    try {
      const checkIfExists = await checkIfLogoExists(path);
      if (checkIfExists) {
        await deleteObject(storageRef);
      }
      const resizedFile: any = await resizeFile(file, size, type);
      await uploadBytes(storageRef, resizedFile);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.log(error);
      return "";
    }
  };

  // Upload plant enum to firestore collection 'plantEnums'
  const updatePlantEnum = async () => {
    setDialogLoading(true);
    try {
      // Compare iconPath to plantEnum.icon
      console.log("Updating icon");
      let tempIconPath;
      if (iconPath) {
        console.log("Iconpath exists");
        if (iconPath.isUrl) {
          console.log("Iconpath is url");
          // If iconPath is a url, set icon to url
          tempIconPath = iconPath.url;
        } else {
          console.log("Iconpath is file");
          // If iconPath is a file, upload file and set icon to url
          const iconPathString = `cloud/plantEnums/${plantEnumInitData.id}/${plantEnumInitData.id}-icon.png`;
          const uploadIcon = await uploadImage(
            iconPath,
            iconPathString,
            500,
            "png"
          );
          tempIconPath = uploadIcon;
        }
      } else {
        console.log("Iconpath does not exist");
        tempIconPath = "";
        // If iconPath is empty, check if plantEnum.icon exists and delete
        if (plantEnumInitData.icon) {
          const iconRef = ref(
            firebaseStorage,
            `cloud/plantEnums/${plantEnumInitData.id}/${plantEnumInitData.id}-icon.png`
          );
          await deleteObject(iconRef);
        }
      }

      // Compare imagePath to plantEnum.image
      console.log("Updating image");
      let tempImagePath;
      if (imagePath) {
        console.log("Imagepath exists");
        if (imagePath.isUrl) {
          console.log("Imagepath is url");
          // If imagePath is a url, set image to url
          tempImagePath = imagePath.url;
        } else {
          console.log("Imagepath is file");
          // If imagePath is a file, upload file and set image to url
          const imagePathString = `cloud/plantEnums/${plantEnumInitData.id}/${plantEnumInitData.id}.jpeg`;
          const uploadImageTemp = await uploadImage(
            imagePath,
            imagePathString,
            1000,
            "jpeg"
          );
          tempImagePath = uploadImageTemp;
        }
      } else {
        console.log("Imagepath does not exist");
        tempImagePath = "";
        // If imagePath is empty, check if plantEnum.image exists and delete
        if (plantEnumInitData.image) {
          const imageRef = ref(
            firebaseStorage,
            `cloud/plantEnums/${plantEnumInitData.id}/${plantEnumInitData.id}.jpeg`
          );
          await deleteObject(imageRef);
        }
      }

      // Compare imagePathArray to plantEnum.images
      console.log("Updating image array");
      let tempImagePathArray = [];
      if (imagePathArray.length > 0) {
        console.log("ImagepathArray exists");
        for (let i = 0; i < imagePathArray.length; i++) {
          console.log("ImagepathArray loop index: ", i);
          if (imagePathArray[i].isUrl) {
            console.log("ImagepathArray index is url");
            // If imagePathArray is a url, compare to plantEnum.images
            if (plantEnumInitData.images[i] === imagePathArray[i].url) {
              console.log("ImagepathArray index is same as url");
              // If url is the same, set image to url
              tempImagePathArray.push(imagePathArray[i].url);
            } else {
              console.log("ImagepathArray index is different from url");
              // If url is different, fetch image from url and upload file and set image to url
              const fetchImgFromUrl = await fetch(imagePathArray[i].url);
              const blob = await fetchImgFromUrl.blob();
              const newFile = new File(
                [blob],
                `${plantEnumInitData.id}-${i + 1}.jpeg`,
                { type: "image/jpeg" }
              );
              const imagePathString = `cloud/plantEnums/${
                plantEnumInitData.id
              }/${plantEnumInitData.id}-${i + 1}.jpeg`;
              const uploadImageTemp = await uploadImage(
                newFile,
                imagePathString,
                1000,
                "jpeg"
              );
              tempImagePathArray.push(uploadImageTemp);
            }
          } else {
            console.log("ImagepathArray index is file");
            // If imagePathArray is a file, upload file and set image to url
            const imagePathString = `cloud/plantEnums/${plantEnumInitData.id}/${
              plantEnumInitData.id
            }-${i + 1}.jpeg`;
            const uploadImageTemp = await uploadImage(
              imagePathArray[i],
              imagePathString,
              1000,
              "jpeg"
            );
            tempImagePathArray.push(uploadImageTemp);
          }
        }
      }

      // If imagePathArray is shorter than plantEnum.images, delete the rest
      console.log("Checking if image array is shorter than plantEnum.images");
      if (imagePathArray.length < plantEnumInitData.images.length) {
        console.log("Image array is shorter than plantEnum.images");
        for (
          let i = imagePathArray.length;
          i < plantEnumInitData.images.length;
          i++
        ) {
          console.log("Deleting image from plantEnum.images");
          const imageRef = ref(
            firebaseStorage,
            `cloud/plantEnums/${plantEnumInitData.id}/${plantEnumInitData.id}-${
              i + 1
            }.jpeg`
          );
          await deleteObject(imageRef);
        }
      }

      console.log("Updating plant enum");

      const plantEnum = {
        name: plantName,
        scientificName: scientificName,
        subtitle: plantSubtitle,
        description: plantDescription,
        category: category,
        growTimeBaby: {
          min: growTimeBabyMin,
          max: growTimeBabyMax,
        },
        growTimeVegetative: {
          min: growTimeVegetativeMin,
          max: growTimeVegetativeMax,
        },
        growTimeFlowering: {
          min: floweringMin,
          max: floweringMax,
        },
        color: colorList,
        flavour: flavourList,
        nutrients: nutrientList,
        kitchen: kitchenList,
        allergeens: allergeenList,
        isEdible: isEdible,
      };
      updateDoc(doc(firebaseStore, "plantEnums", plantEnumInitData.id), {
        ...plantEnum,
        icon: tempIconPath,
        image: tempImagePath,
        images: tempImagePathArray,
      });

      setSnackbarOpen(true);
      setSnackbarMessage(`${plantName} updated successfully`);
      setDialogLoading(false);
    } catch (error) {
      console.log(error);
      setDialogLoading(false);
      setSnackbarOpen(true);
      setSnackbarMessage("Error updating plant enum");
    }
  };

  console.log(imagePathArray);
  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          width: "90vw",
          maxWidth: "1000px",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <div className="AddPlantEnumDialog">
        <DialogTitle>Edit plant enum</DialogTitle>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Plant name"
            variant="outlined"
            value={plantName}
            onChange={(e) => setPlantName(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Scientific name"
            variant="outlined"
            value={scientificName}
            onChange={(e) => setScientificName(e.target.value)}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Plant subtitle"
            variant="outlined"
            value={plantSubtitle}
            onChange={(e) => setPlantSubtitle(e.target.value)}
            multiline
            rows={2}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Plant description"
            variant="outlined"
            value={plantDescription}
            onChange={(e) => setPlantDescription(e.target.value)}
            multiline
            rows={4}
          />
        </div>
        <div className="AddPlantEnumDialogInner">
          <TextField
            sx={primaryColorTextField}
            size="small"
            fullWidth
            label="Category"
            variant="outlined"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            select
          >
            {plantCategoryEnums.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <div className="AddPlantEnumDialogOuter" style={{ marginTop: "40px" }}>
          <p className="AddPlantEnumDialogOuterTitle">
            Grow time seedling or cutting
          </p>
          <div className="AddPlantEnumDialogInner">
            <div className="AddPlantEnumDialogCounter">
              <p className="AddPlantEnumDialogOuterSub">Minimum days</p>
              <div className="AddPlantEnumDialogCounterInner">
                <IconButton
                  onClick={() => {
                    if (
                      growTimeBabyMin - 1 >= 0 &&
                      growTimeBabyMin <= growTimeBabyMax
                    ) {
                      setGrowTimeBabyMin(growTimeBabyMin - 1);
                    }
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  sx={{
                    ...primaryColorTextField,
                    width: "80px",
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: "center" } }}
                  variant="outlined"
                  value={growTimeBabyMin}
                  onChange={(e) => {
                    if (
                      Number(e.target.value) >= 0 &&
                      Number(e.target.value) <= growTimeBabyMax
                    ) {
                      setGrowTimeBabyMin(Number(e.target.value));
                    }
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (growTimeBabyMin < growTimeBabyMax) {
                      setGrowTimeBabyMin(growTimeBabyMin + 1);
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
            <div className="AddPlantEnumDialogCounter">
              <p className="AddPlantEnumDialogOuterSub">Maximum days</p>
              <div className="AddPlantEnumDialogCounterInner">
                <IconButton
                  onClick={() => {
                    if (growTimeBabyMax - 1 >= growTimeBabyMin) {
                      setGrowTimeBabyMax(growTimeBabyMax - 1);
                    }
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  sx={{
                    ...primaryColorTextField,
                    width: "80px",
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: "center" } }}
                  variant="outlined"
                  value={growTimeBabyMax}
                  onChange={(e) => {
                    if (
                      Number(e.target.value) >= growTimeBabyMin &&
                      Number(e.target.value) >= 0
                    ) {
                      setGrowTimeBabyMax(Number(e.target.value));
                    }
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (growTimeBabyMax >= growTimeBabyMin) {
                      setGrowTimeBabyMax(growTimeBabyMax + 1);
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter">
          <p className="AddPlantEnumDialogOuterTitle">Grow time vegetative</p>
          <div className="AddPlantEnumDialogInner">
            <div className="AddPlantEnumDialogCounter">
              <p className="AddPlantEnumDialogOuterSub">Minimum days</p>
              <div className="AddPlantEnumDialogCounterInner">
                <IconButton
                  onClick={() => {
                    if (
                      growTimeVegetativeMin - 1 >= 0 &&
                      growTimeVegetativeMin <= growTimeVegetativeMax
                    ) {
                      setGrowTimeVegetativeMin(growTimeVegetativeMin - 1);
                    }
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  sx={{
                    ...primaryColorTextField,
                    width: "80px",
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: "center" } }}
                  variant="outlined"
                  value={growTimeVegetativeMin}
                  onChange={(e) => {
                    if (
                      Number(e.target.value) >= 0 &&
                      Number(e.target.value) <= growTimeVegetativeMax
                    ) {
                      setGrowTimeVegetativeMin(Number(e.target.value));
                    }
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (growTimeVegetativeMin < growTimeVegetativeMax) {
                      setGrowTimeVegetativeMin(growTimeVegetativeMin + 1);
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
            <div className="AddPlantEnumDialogCounter">
              <p className="AddPlantEnumDialogOuterSub">Maximum days</p>
              <div className="AddPlantEnumDialogCounterInner">
                <IconButton
                  onClick={() => {
                    if (growTimeVegetativeMax - 1 >= growTimeVegetativeMin) {
                      setGrowTimeVegetativeMax(growTimeVegetativeMax - 1);
                    }
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  sx={{
                    ...primaryColorTextField,
                    width: "80px",
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: "center" } }}
                  variant="outlined"
                  value={growTimeVegetativeMax}
                  onChange={(e) => {
                    if (
                      Number(e.target.value) >= growTimeVegetativeMin &&
                      Number(e.target.value) >= 0
                    ) {
                      setGrowTimeVegetativeMax(Number(e.target.value));
                    }
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (growTimeVegetativeMax >= growTimeVegetativeMin) {
                      setGrowTimeVegetativeMax(growTimeVegetativeMax + 1);
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter">
          <p className="AddPlantEnumDialogOuterTitle">Grow time flowering</p>
          <div className="AddPlantEnumDialogInner">
            <div className="AddPlantEnumDialogCounter">
              <p className="AddPlantEnumDialogOuterSub">Minimum days</p>
              <div className="AddPlantEnumDialogCounterInner">
                <IconButton
                  onClick={() => {
                    if (floweringMin - 1 >= 0 && floweringMin <= floweringMax) {
                      setFloweringMin(floweringMin - 1);
                    }
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  sx={{
                    ...primaryColorTextField,
                    width: "80px",
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: "center" } }}
                  variant="outlined"
                  value={floweringMin}
                  onChange={(e) => {
                    if (
                      Number(e.target.value) >= 0 &&
                      Number(e.target.value) <= floweringMax
                    ) {
                      setFloweringMin(Number(e.target.value));
                    }
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (floweringMin < floweringMax) {
                      setFloweringMin(floweringMin + 1);
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
            <div className="AddPlantEnumDialogCounter">
              <p className="AddPlantEnumDialogOuterSub">Maximum days</p>
              <div className="AddPlantEnumDialogCounterInner">
                <IconButton
                  onClick={() => {
                    if (floweringMax - 1 >= floweringMin) {
                      setFloweringMax(floweringMax - 1);
                    }
                  }}
                >
                  <RemoveIcon />
                </IconButton>
                <TextField
                  sx={{
                    ...primaryColorTextField,
                    width: "80px",
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: "center" } }}
                  variant="outlined"
                  value={floweringMax}
                  onChange={(e) => {
                    if (
                      Number(e.target.value) >= floweringMin &&
                      Number(e.target.value) >= 0
                    ) {
                      setFloweringMax(Number(e.target.value));
                    }
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (floweringMax >= floweringMin) {
                      setFloweringMax(floweringMax + 1);
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter" style={{ marginTop: "40px" }}>
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Color"
              variant="outlined"
              value={colorActual}
              onChange={(e) => setColorActual(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() =>
                handleAddToList(
                  colorActual,
                  colorList,
                  setColorList,
                  setColorActual
                )
              }
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogList">
            {colorList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">{item}</p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() =>
                    handleRemoveFromList(index, colorList, setColorList)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter">
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Flavour"
              variant="outlined"
              value={flavourActual}
              onChange={(e) => setFlavourActual(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() =>
                handleAddToList(
                  flavourActual,
                  flavourList,
                  setFlavourList,
                  setFlavourActual
                )
              }
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogList">
            {flavourList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">{item}</p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() =>
                    handleRemoveFromList(index, flavourList, setFlavourList)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter">
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Nutrients"
              variant="outlined"
              value={nutrientActual}
              onChange={(e) => setNutrientActual(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() =>
                handleAddToList(
                  nutrientActual,
                  nutrientList,
                  setNutrientList,
                  setNutrientActual
                )
              }
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogList">
            {nutrientList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">{item}</p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() =>
                    handleRemoveFromList(index, nutrientList, setNutrientList)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter">
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Kitchen"
              variant="outlined"
              value={kitchenActual}
              onChange={(e) => setKitchenActual(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() =>
                handleAddToList(
                  kitchenActual,
                  kitchenList,
                  setKitchenList,
                  setKitchenActual
                )
              }
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogList">
            {kitchenList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">{item}</p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() =>
                    handleRemoveFromList(index, kitchenList, setKitchenList)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogOuter">
          <div className="AddPlantEnumDialogInner">
            <TextField
              sx={primaryColorTextField}
              size="small"
              fullWidth
              label="Allergeens"
              variant="outlined"
              value={allergeenActual}
              onChange={(e) => setAllergeenActual(e.target.value)}
            />
            <IconButton
              sx={{ marginLeft: "10px" }}
              onClick={() =>
                handleAddToList(
                  allergeenActual,
                  allergeenList,
                  setAllergeenList,
                  setAllergeenActual
                )
              }
            >
              <AddIcon sx={{ color: "#44a99a" }} />
            </IconButton>
          </div>
          <div className="AddPlantEnumDialogList">
            {allergeenList.map((item, index) => (
              <div key={index} className="AddPlantEnumDialogListItem">
                <p className="AddPlantEnumDialogListItemText">{item}</p>
                <IconButton
                  sx={{ marginLeft: "10px" }}
                  onClick={() =>
                    handleRemoveFromList(index, allergeenList, setAllergeenList)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="AddPlantEnumDialogInner" style={{ marginTop: "40px" }}>
          <p className="AddPlantEnumDialogSwitchText">Is edible</p>
          <Switch
            checked={isEdible}
            onChange={(e) => setIsEdible(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#44a99a",
              },

              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#44a99a",
              },
            }}
          />
        </div>
        <div className="AddPlantEnumDialogOuter" style={{ marginTop: "40px" }}>
          <p className="AddPlantEnumDialogOuterTitle">Icon</p>
          <div {...getRootProps({ className: "Dropzone" })}>
            <input {...getInputProps()} />
            <p className="AddPlantEnumDialogOuterSub">
              Drag 'n' drop a Icon-image here, or click to select
            </p>
          </div>
          {iconPath && (
            <div className="AddPlantEnumDialogList">
              <p
                className="AddPlantEnumDialogOuterTitle"
                style={{ marginBottom: "10px" }}
              >
                Chosen icon - Click to delete
              </p>
              {iconPath.isUrl ? (
                <div
                  onClick={() => setIconPath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader imgUrl={iconPath.url} radiusStyle={"5px"} />
                </div>
              ) : (
                <div
                  onClick={() => setIconPath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader
                    imgUrl={URL.createObjectURL(iconPath)}
                    radiusStyle={"5px"}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="AddPlantEnumDialogOuter">
          <p className="AddPlantEnumDialogOuterTitle">Thumbnail</p>
          <div {...getRootPropsImage({ className: "Dropzone" })}>
            <input {...getInputPropsImage()} />
            <p className="AddPlantEnumDialogOuterSub">
              Drag 'n' drop a thumbnail-image here, or click to select
            </p>
          </div>
          {imagePath && (
            <div className="AddPlantEnumDialogList">
              <p
                className="AddPlantEnumDialogOuterTitle"
                style={{ marginBottom: "10px" }}
              >
                Chosen thumbnail - Click to delete
              </p>
              {imagePath.isUrl ? (
                <div
                  onClick={() => setImagePath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader imgUrl={imagePath.url} radiusStyle={"5px"} />
                </div>
              ) : (
                <div
                  onClick={() => setImagePath(null)}
                  className="AddPlantEnumDialogListImage"
                >
                  <ImageLoader
                    imgUrl={URL.createObjectURL(imagePath)}
                    radiusStyle={"5px"}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="AddPlantEnumDialogOuter">
          <p className="AddPlantEnumDialogOuterTitle">Images</p>
          <div {...getRootPropsImageArray({ className: "Dropzone" })}>
            <input {...getInputPropsImageArray()} />
            <p className="AddPlantEnumDialogOuterSub">
              Drag 'n' drop up to 5 images here, or click to select
            </p>
          </div>
          {imagePathArray.length > 0 && (
            <p className="AddPlantEnumDialogOuterTitle">
              Chosen images - Click to delete
            </p>
          )}
          {imagePathArray.length > 0 && (
            <div className="AddPlantEnumDialogImageList">
              {imagePathArray.map((item, index) =>
                item.isUrl ? (
                  <div
                    key={index}
                    onClick={() => onDeleteFromImageArray(index)}
                    className="AddPlantEnumDialogListImage"
                  >
                    <ImageLoader imgUrl={item.url} radiusStyle={"5px"} />
                  </div>
                ) : (
                  <div
                    key={index}
                    onClick={() => onDeleteFromImageArray(index)}
                    className="AddPlantEnumDialogListImage"
                  >
                    <ImageLoader
                      imgUrl={URL.createObjectURL(item)}
                      radiusStyle={"5px"}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
        <div
          className="AddPlantEnumDialogInner"
          style={{ marginTop: "60px", justifyContent: "flex-end" }}
        >
          <Button
            disabled={dialogLoading}
            sx={{ marginLeft: "20px", marginRight: "20px" }}
            color="inherit"
            onClick={onReset}
          >
            RESTORE
          </Button>
          <Button
            disabled={dialogLoading}
            sx={{ marginLeft: "20px", marginRight: "20px" }}
            color="inherit"
            onClick={updatePlantEnum}
          >
            {dialogLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "UPDATE"
            )}
          </Button>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
      />
    </Dialog>
  );
}

export default EditPlantEnum;
